import { Capacitor } from '@capacitor/core';

const DEFAULT_API_BASE_URL = 'http://localhost:5000';
const ANDROID_EMULATOR_API_BASE_URL = 'http://10.0.2.2:5000';

const normalizeText = (value) => String(value || '').trim();

const parseApiCandidates = (rawValue) =>
  normalizeText(rawValue)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

const isLocalHostName = (hostname) => {
  const normalized = normalizeText(hostname).toLowerCase();
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1';
};

const tryParseUrl = (value) => {
  try {
    return new URL(value);
  } catch (error) {
    return null;
  }
};

const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

const getRuntimePlatform = () => {
  if (!isNativePlatform()) {
    return 'web';
  }
  try {
    return normalizeText(Capacitor.getPlatform()).toLowerCase() || 'native';
  } catch (error) {
    return 'native';
  }
};

const inferRuntimeEnvFromWindow = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const host = normalizeText(window.location?.hostname);
  return isLocalHostName(host) ? 'development' : 'production';
};

const getRuntimeEnv = () => {
  const explicit = normalizeText(process.env.REACT_APP_RUNTIME_ENV).toLowerCase();
  if (explicit === 'development' || explicit === 'production') {
    return explicit;
  }

  if (!isNativePlatform()) {
    const inferredFromWindow = inferRuntimeEnvFromWindow();
    if (inferredFromWindow) {
      return inferredFromWindow;
    }
  }

  return process.env.NODE_ENV === 'development' ? 'development' : 'production';
};

const firstDefinedValue = (values) => {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) {
      return normalized;
    }
  }
  return '';
};

const resolveFromLegacyApiList = (rawValue) => {
  const candidates = parseApiCandidates(rawValue);
  if (!candidates.length) {
    return '';
  }

  if (candidates.length === 1 || typeof window === 'undefined') {
    return candidates[0];
  }

  const currentHost = normalizeText(window.location?.hostname);
  const currentPort = normalizeText(window.location?.port);
  const isCurrentLocal = isLocalHostName(currentHost);

  if (isCurrentLocal) {
    const localCandidates = candidates.filter((candidate) => {
      const parsed = tryParseUrl(candidate);
      return parsed ? isLocalHostName(parsed.hostname) : false;
    });

    const preferredLocalCandidate = localCandidates.find((candidate) => {
      const parsed = tryParseUrl(candidate);
      if (!parsed) {
        return false;
      }
      const candidatePort = normalizeText(parsed.port);
      return candidatePort && candidatePort !== currentPort;
    });

    if (preferredLocalCandidate) {
      return preferredLocalCandidate;
    }

    if (localCandidates.length) {
      return DEFAULT_API_BASE_URL;
    }
  }

  const sameProtocolNonLocalCandidate = candidates.find((candidate) => {
    const parsed = tryParseUrl(candidate);
    if (!parsed) {
      return false;
    }
    return !isLocalHostName(parsed.hostname) && parsed.protocol === window.location.protocol;
  });

  if (sameProtocolNonLocalCandidate) {
    return sameProtocolNonLocalCandidate;
  }

  const firstNonLocalCandidate = candidates.find((candidate) => {
    const parsed = tryParseUrl(candidate);
    return parsed ? !isLocalHostName(parsed.hostname) : false;
  });

  return firstNonLocalCandidate || candidates[0];
};

export const RUNTIME_PLATFORM = getRuntimePlatform();
export const RUNTIME_ENV = getRuntimeEnv();

export const resolveApiBaseUrl = () => {
  const isAndroid = RUNTIME_PLATFORM === 'android';
  const isWeb = RUNTIME_PLATFORM === 'web';
  const isDev = RUNTIME_ENV === 'development';

  const platformSpecificApi = firstDefinedValue([
    isAndroid && isDev ? process.env.REACT_APP_API_URL_ANDROID_DEV : '',
    isAndroid && !isDev ? process.env.REACT_APP_API_URL_ANDROID_PROD : '',
    isAndroid ? process.env.REACT_APP_API_URL_ANDROID : '',
    isWeb && isDev ? process.env.REACT_APP_API_URL_WEB_DEV : '',
    isWeb && !isDev ? process.env.REACT_APP_API_URL_WEB_PROD : '',
    isWeb ? process.env.REACT_APP_API_URL_WEB : '',
  ]);
  if (platformSpecificApi) {
    return platformSpecificApi;
  }

  const legacyApiValue = normalizeText(process.env.REACT_APP_API_URL);
  if (legacyApiValue) {
    const fromLegacyList = resolveFromLegacyApiList(legacyApiValue);
    if (fromLegacyList) {
      return fromLegacyList;
    }
  }

  if (isAndroid && isDev) {
    return ANDROID_EMULATOR_API_BASE_URL;
  }

  return DEFAULT_API_BASE_URL;
};

export const resolveGoogleClientId = () => {
  const isAndroid = RUNTIME_PLATFORM === 'android';
  const isWeb = RUNTIME_PLATFORM === 'web';
  const isDev = RUNTIME_ENV === 'development';

  return firstDefinedValue([
    isAndroid && isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_ANDROID_DEV : '',
    isAndroid && !isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_ANDROID_PROD : '',
    isAndroid ? process.env.REACT_APP_GOOGLE_CLIENT_ID_ANDROID : '',
    isWeb && isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB_DEV : '',
    isWeb && !isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB_PROD : '',
    isWeb ? process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB : '',
    process.env.REACT_APP_GOOGLE_CLIENT_ID,
  ]);
};

export const resolveGoogleWebClientId = () => {
  const isDev = RUNTIME_ENV === 'development';

  return firstDefinedValue([
    isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB_DEV : '',
    !isDev ? process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB_PROD : '',
    process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB,
    process.env.REACT_APP_GOOGLE_CLIENT_ID,
  ]);
};

export const API_BASE_URL = resolveApiBaseUrl();
export const GOOGLE_CLIENT_ID = resolveGoogleClientId();
export const GOOGLE_WEB_CLIENT_ID = resolveGoogleWebClientId();
